<script lang="ts">
import { SettingsService } from '@/services/settings'
import type { User } from '@/types'
import type { TIcon } from '@/utils/helper-objects'
import { ThemeType } from '@goodlifeplus/themes'
import { type PropType, defineComponent } from 'vue'

import DepositModal from '../modals/DepositModal.vue'

/**
 * SidebarBalanceCard
 */
export default defineComponent({
  components: {
    DepositModal,
  },
  props: {
    user: {
      type: Object as PropType<User>,
      required: true,
    },
  },
  computed: {
    depositEnabled(): boolean {
      return SettingsService.isSectionEnabled('deposit')
    },
    /**
     * Subscription sites (e.g. True North) have no instant prizes, so balances
     * and deposits are not relevant — only points and membership are shown.
     */
    isSubscription(): boolean {
      return this.$theme.type === ThemeType.Subscription
    },
    subscriptionIcon(): TIcon {
      switch (this.$userStore.highestPlanTier) {
        case 'standard':
          return this.Icons.Star
        case 'platinum':
        case 'premium':
          return this.Icons.Lightening
        case 'vip':
        case 'legend':
          return this.Icons.Crown
        default:
          return this.Icons.Star
      }
    },
    badgeClass(): string {
      switch (this.$userStore.highestPlanTier) {
        case 'standard':
          return '!standard-badge !text-pure-white'
        case 'platinum':
        case 'premium':
          return '!platinum-badge !text-pure-white'
        case 'vip':
        case 'legend':
          return 'bg-gradient-gold !text-black'
        default:
          return ''
      }
    },
  },
})
</script>

<template>
  <div
    class="balance-card flex flex-col gap-3 rounded-xl p-3 border border-[#B6C6CF] bg-pure-white"
  >
    <div class="flex justify-between items-center self-stretch">
      <p class="font-bold text-xl">
        {{ user.first_name }}
        {{ user.last_name?.charAt(0).toLocaleUpperCase() }}.
      </p>

      <AppBadge
        v-if="
          $userStore.highestPlanTier &&
          $userStore.highestPlanTier?.toLowerCase() !== 'none'
        "
        size="md"
        class="!rounded-md !px-2 !py-1 capitalize !leading-none flex gap-1"
        :class="badgeClass"
      >
        <AppIcon :icon="subscriptionIcon" size="xs" />
        {{ $t(`membership_badge.${$userStore.highestPlanTier}`) }}
      </AppBadge>
    </div>

    <div
      v-if="!isSubscription"
      class="grid grid-cols-[1fr_auto] gap-x-4 gap-y-3"
    >
      <span class="font-extrabold">Current Balance: </span>
      <strong class="text-right">{{
        $n(user.credit.totals.overall / 100, 'currency')
      }}</strong>
    </div>
    <div class="grid grid-cols-[1fr_auto] gap-x-4">
      <span class="font-extrabold">Points Balance: </span>
      <strong class="text-right">{{
        $n(user.eligible_points ?? 0, 'number')
      }}</strong>
    </div>

    <div class="flex flex-col gap-2">
      <AppTextButton
        :variant="UI.TextButton.Variant.Tertiary"
        :size="UI.TextButton.Size.Medium"
        label="My Points Area"
        :route="{ name: 'members-zone-rewards' }"
        :class="{ 'points-area-button': isSubscription }"
      />
      <DepositModal v-if="depositEnabled && !isSubscription">
        <template #button="{ openModal }">
          <AppTextButton
            :variant="UI.TextButton.Variant.Primary"
            label="Deposit Now"
            @click="openModal"
          />
        </template>
      </DepositModal>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.points-area-button {
  --glp-button-text-font-weight: 900;
}

.property-list:deep(.contents) {
  span {
    @apply font-extrabold italic;

    &:nth-child(2) {
      @apply not-italic;
    }
  }
  @apply gap-y-3 leading-5;
}
</style>
